import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import PageLayout from './layout/PageLayout';
import NovelCard from './NovelCard';
import HeroSection from './HeroSection/HeroSection.jsx';
import HorizontalScrollList from './HorizontalScrollList.jsx';
import ContinueReading from './ContinueReading';
import AnnouncementBanner from './AnnouncementBanner';
import ContestStrip from './ContestStrip';
import HomePageSkeleton from './HomePageSkeleton';
import HeroSkeleton from './HeroSkeleton';
import useAsync from '../hooks/useAsync';
import useViewerIdentity from '../hooks/useViewerIdentity';
import { getHomePageNovels, getCommunityFavorites } from '../services/novelsApi';
import { getFollowedAuthorsFeed } from '../services/followsApi';
import { getNovelImageUrl } from '../utils/imageUtils';
import Button from './common/ui/Button';
import Seo from './common/Seo';
import { BRAND_NAME } from '../constants/brand';
import './HomePage.css';

const fetchHomeData = async () => {
  const [homeFeed, communityFavorites] = await Promise.all([
    getHomePageNovels(),
    getCommunityFavorites(),
  ]);

  return {
    ...homeFeed,
    communityFavorites,
  };
};

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useViewerIdentity();

  const { data: homeData, loading, error, execute } = useAsync(fetchHomeData, { immediate: false });

  const [followedFeed, setFollowedFeed] = useState([]);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    execute();
  }, [execute]);

  // Fetch followed authors feed for logged-in users
  useEffect(() => {
    if (!user?.id) {
      setFollowedFeed([]);
      return;
    }
    getFollowedAuthorsFeed(user.id)
      .then(data => setFollowedFeed(Array.isArray(data) ? data.filter((n) => n && getNovelImageUrl(n)) : []))
      .catch(() => setFollowedFeed([]));
  }, [user?.id]);

  const sections = useMemo(() => {
    const withCovers = (novels) =>
      (novels ?? []).filter((n) => n && getNovelImageUrl(n));
    return {
      latest: withCovers(homeData?.latestNovels),
      trending: withCovers(homeData?.trendingNovels),
      completed: withCovers(homeData?.completedNovels),
      favorites: withCovers(homeData?.communityFavorites),
      fresh: withCovers(homeData?.freshNovels),
      featured: withCovers(homeData?.featuredNovels),
    };
  }, [homeData]);

  // Use top 5 trending as highlights for the hero
  const heroNovels = useMemo(() => {
    // Prefer trending, then completed, then latest
    const pool = [...sections.trending, ...sections.completed, ...sections.latest];
    const seen = new Set();
    const unique = [];

    for (const novel of pool) {
      if (!novel || seen.has(novel.id)) continue;
      // All sections are already filtered to only include novels with covers,
      // so no need to re-check getNovelImageUrl here.
      seen.add(novel.id);
      unique.push(novel);
      if (unique.length === 5) break;
    }
    return unique;
  }, [sections]);

  const formatChapters = (totalChapters) => (
    typeof totalChapters === 'number' && totalChapters > 0
      ? `${totalChapters} Chapters`
      : 'Chapters updating'
  );

  const getStatusLabel = (novel) => (novel?.isCompleted ? 'Completed' : 'Ongoing');

  const renderCard = (novel, loading = 'lazy') => (
    <NovelCard
      key={novel.id}
      novel={novel}
      to={`/novels/${novel.id}`}
      status={getStatusLabel(novel)}
      genres={Array.from(novel.genres || [])}
      variant="standard" // Ensure standard variant
      loading={loading}
    />
  );

  if (error) {
    return (
      <PageLayout>
        <h1 className="visually-hidden">{BRAND_NAME} — Read Web Novels & Light Novels Online</h1>
        <div className="home-page__state home-page__state--error">
          <p>Unable to load homepage content. Please try again later.</p>
          <Button variant="primary" onClick={execute}>
            Retry
          </Button>
        </div>
      </PageLayout>
    );
  }

  // Show a loading spinner for the novel sections while the homepage data
  // is being fetched. We do NOT early-return here because that would unmount
  // AnnouncementBanner, ContestStrip, and ContinueReading, causing them to
  // re-fetch their data when the homepage data arrives and the full tree
  // re-renders (duplicate API calls).
  //
  // Key: use !homeData (not loading && !homeData) so the skeleton renders on
  // the very first paint. useAsync is called with { immediate: false }, so
  // loading starts as false — if we used loading && !homeData, the first
  // paint would show NO skeleton (empty page), then the useEffect fires,
  // loading becomes true, and the skeleton appears — causing a massive
  // footer shift. By checking !homeData, the skeleton is present from the
  // first paint, and only disappears when real data arrives.
  const showNovelSpinner = !homeData;

  return (
    <PageLayout noMobilePadding>
      <Seo
        title="Home"
        description={`Discover trending web novels, fresh arrivals, and community favorites. Read and bookmark your favorite stories on ${BRAND_NAME}.`}
        url="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: BRAND_NAME,
          url: 'https://novelday.net',
          description: 'Discover, read, and share captivating web novels and light novels.',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://novelday.net/search?query={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      <div className="home-page">
        {/* Always-present H1 for accessibility/SEO. This is the single
            page-level heading — it stays stable regardless of which novel
            the hero carousel is currently featuring. HeroSection renders
            the featured novel's title as an <h2> (a featured item, not the
            page heading), so there is exactly one <h1> on the page in every
            state (loading, no covers, hero present, hero absent). The error
            branch above has its own hidden H1. */}
        <h1 className="visually-hidden">{BRAND_NAME} — Read Web Novels & Light Novels Online</h1>

        {/* HERO SECTION — render a skeleton placeholder at this DOM
            position while data is loading so the hero's vertical space is
            reserved. Without this, the hero mounts only after the API
            responds, pushing AnnouncementBanner and everything below it
            down by ~377px and causing a large CLS on mobile. */}
        {showNovelSpinner ? (
          <HeroSkeleton />
        ) : heroNovels.length > 0 ? (
          <HeroSection novels={heroNovels} />
        ) : null}

        {/* ANNOUNCEMENT BANNER — below hero per v3 design */}
        <AnnouncementBanner />

        {/* CONTINUE READING SECTION */}
        <ContinueReading />

        {showNovelSpinner ? (
          <HomePageSkeleton />
        ) : (
          <>
            {/* TRENDING SWIMLANE */}
            {sections.trending.length > 0 && (
              <HorizontalScrollList
                title="Trending"
                accentTitle="Now"
                actionLabel="View all"
                onAction={() => navigate('/search?sort=rankScore,DESC')}
              >
                {sections.trending.map((novel) => renderCard(novel, 'eager'))}
              </HorizontalScrollList>
            )}

            {/* LIVE CONTESTS — right after Trending */}
            <ContestStrip />

            {/* PROMOTERS' PICKS — novels from authors who promote NovelDay */}
            {sections.featured.length > 0 && (
              <HorizontalScrollList
                title="Promoters'"
                accentTitle="Picks"
                infoText="Novels by authors who refer new readers and authors to NovelDay. Share your referral link from your profile to feature yours here."
                actionLabel="View all"
                onAction={() => navigate('/library')}
              >
                {sections.featured.map((novel) => renderCard(novel))}
              </HorizontalScrollList>
            )}

            {/* FRESH FINDS — newly published novels (last 30 days) */}
            {sections.fresh.length > 0 && (
              <HorizontalScrollList
                title="Fresh"
                accentTitle="Finds"
                actionLabel="View all"
                onAction={() => navigate('/search?sort=createdAt,DESC')}
              >
                {sections.fresh.map((novel) => renderCard(novel))}
              </HorizontalScrollList>
            )}

            {/* FROM AUTHORS YOU FOLLOW — only for logged-in users */}
            {user && followedFeed.length > 0 && (
              <HorizontalScrollList
                title="From Authors"
                accentTitle="You Follow"
                actionLabel="Discover"
                onAction={() => navigate('/library')}
              >
                {followedFeed.map((novel) => renderCard(novel))}
              </HorizontalScrollList>
            )}

            {/* COMMUNITY FAVORITES SWIMLANE */}
            {sections.favorites.length > 0 && (
              <HorizontalScrollList
                title="Community"
                accentTitle="Favorites"
                actionLabel="View all"
                onAction={() => navigate('/search?sort=averageRating,DESC')}
              >
                {sections.favorites.map((novel) => renderCard(novel))}
              </HorizontalScrollList>
            )}

            {/* COMPLETED SWIMLANE */}
            {sections.completed.length > 0 && (
              <HorizontalScrollList
                title="Bingeworthy"
                accentTitle="Completed"
                actionLabel="View all"
                onAction={() => navigate('/search?isCompleted=completed')}
              >
                {sections.completed.map((novel) => renderCard(novel))}
              </HorizontalScrollList>
            )}

            <div className="container">
              {/* LATEST GRID (Standard Layout) */}
              <section className="home-section">
                <header className="home-section__header">
                  <h2 className="home-section__heading">Just <span className="home-section__accent">Updated</span></h2>
                  <Link
                    to="/library"
                    className="home-section__view-all"
                  >
                    Latest grid <span className="home-section__arrow">&rarr;</span>
                  </Link>
                </header>

                <div className="home-collection__grid">
                  {sections.latest.map((novel, index) =>
                    renderCard(novel, index < 2 ? 'eager' : 'lazy')
                  )}
                </div>
              </section>
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
}

export default HomePage;
